/**
 * @ken/auth — auth context for the Ken cockpit.
 *
 * Per ADR-030 + KEN_LAYER_7 §7.1, §8.1.
 *
 * Provides:
 *   - `useAuth()` — current user identity, role list, permission set,
 *     active tenant.
 *   - `<AuthProvider>` — wraps the app. On mount validates the session
 *     cookie, fetches the session shape, and populates the context.
 *   - `signOut()` — clears the session cookie + the TanStack Query cache
 *     + the Zustand store. See KEN_LAYER_7 §8.8 sign-out checklist.
 *
 * JWT is stored in an HttpOnly Secure SameSite=Strict cookie — never
 * readable by JS, never in localStorage / sessionStorage. The shell talks
 * to /api/shell/auth/session to fetch the resolved permission set after
 * the cookie has been validated by FastAPI middleware.
 *
 * Cross-tab safety (KEN_LAYER_7 §8.1.1): tenant context is URL-derived
 * via the host SPA router, NOT read from this store on every render.
 * The store caches the auth identity, not the active tenant.
 */

import { create } from "zustand";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserIdentity {
  ken_user_id: string;
  email: string;
  display_name: string | undefined;
  roles: readonly string[];
  /**
   * Membership kind for the active (user, tenant) row — multi_company_access
   * (ADR-0089/0090). 'member' is the default seat; 'advisor' is the seat-free
   * external accountant (read-only books, never `shell.approvals.decide`).
   * Populated from `/api/shell/auth/session` `user.membership_type`. The
   * CompanySwitcher pill and the AdvisorBanner read this to render the advisor
   * signal. Optional for backward-compat with pre-mig-0240 test fixtures;
   * consumers treat an absent value as 'member'.
   */
  membership_type?: "member" | "advisor";
}

export interface TenantContext {
  /** ISO 4217 currency code for the active tenant. Read by `useCurrency()`. */
  currency: string;
  /** Display name of the active tenant (from `/api/shell/auth/session`
   *  `tenant.name`). Read by `useTenantName()` for the TopBar company pill.
   *  Optional for backward-compat with pre-name test fixtures. */
  name?: string;
}

export interface AuthSession {
  user: UserIdentity;
  /** Active tenant id from the tenant-bound JWT. undefined during pre-tenant. */
  tenantId: string | undefined;
  /** Effective permission set (role grants ∪ overrides ∖ denies). */
  permissions: ReadonlySet<string>;
  /** Bumped by the server when the permission set changes; client polls/SSE. */
  permissions_version: number;
  /** Modules currently enabled for the active tenant. Reflects the
   *  `enabled_modules` array on /api/shell/auth/session. Empty during
   *  a pre-tenant session. */
  enabledModules: readonly string[];
  /** Tenant-scoped UI-side context (currency, future locale, etc.). Read by
   *  cockpit hooks like `useCurrency()`. Optional for backward compat with
   *  pre-mig-0116 test fixtures; `useCurrency()` falls back to 'CAD' when
   *  absent. */
  tenant?: TenantContext;
  /** Whether the signed-in identity has TOTP two-factor enabled
   *  (mfa_two_factor). Populated from `/api/shell/auth/session` `mfa_enabled`;
   *  identity-level, so it holds across tenant switches. Read by the Settings →
   *  Security page to render the on/off state. Optional (exactOptional-
   *  PropertyTypes-safe) for backward-compat with pre-MFA test fixtures;
   *  `useMfaEnabled()` treats an absent value as `false`. */
  mfa_enabled?: boolean;
}

export interface AuthStoreState {
  status: "idle" | "loading" | "authenticated" | "unauthenticated";
  session: AuthSession | null;
  error: string | null;
}

interface AuthStoreActions {
  setLoading: () => void;
  setSession: (session: AuthSession) => void;
  setUnauthenticated: (error?: string) => void;
  clear: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthStoreState & AuthStoreActions>((set) => ({
  status: "idle",
  session: null,
  error: null,

  setLoading: () => set({ status: "loading", error: null }),

  setSession: (session) =>
    set({ status: "authenticated", session, error: null }),

  setUnauthenticated: (error) =>
    set({ status: "unauthenticated", session: null, error: error ?? null }),

  clear: () => set({ status: "unauthenticated", session: null, error: null }),
}));

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Returns the current auth state. Components that need to react to
 * permission changes read this hook; the shell's SSE listener calls
 * `setSession` when `permissions_version` changes.
 *
 * Each property is selected separately so Zustand's default `Object.is`
 * equality stays stable across renders. Returning a fresh `{status,
 * session, error}` object on every call would re-trigger every consumer
 * on every store update (and on every unrelated state change), causing
 * `Maximum update depth exceeded` in tests and wasted renders in
 * production.
 */
export function useAuth(): AuthStoreState {
  const status = useAuthStore((s) => s.status);
  const session = useAuthStore((s) => s.session);
  const error = useAuthStore((s) => s.error);
  return { status, session, error };
}

/** Returns `true` if the active user holds the named permission. */
export function usePermission(slug: string): boolean {
  return useAuthStore((s) => Boolean(s.session?.permissions.has(slug)));
}

/**
 * Returns `true` if the named module is in the active tenant's
 * enabled_modules list. Pairs with the backend `require_module`
 * dependency on the L7 router.
 */
export function useModuleEnabled(moduleId: string): boolean {
  return useAuthStore((s) =>
    Boolean(s.session?.enabledModules.includes(moduleId))
  );
}

/**
 * Returns the active tenant's ISO 4217 currency code (e.g. "CAD", "USD").
 * Defaults to "CAD" during a pre-tenant session.
 *
 * Use together with `<CurrencyValue>` from `@ken/ui` to render money values
 * without ever hardcoding an ISO symbol in component code.
 */
export function useCurrency(): string {
  return useAuthStore((s) => s.session?.tenant?.currency ?? "CAD");
}

/**
 * Returns the active tenant's display name (e.g. "Gourmetwala"), or
 * `undefined` if the session hasn't resolved one. The TopBar company pill
 * falls back to the tenant id, then a generic label, when this is absent.
 */
export function useTenantName(): string | undefined {
  return useAuthStore((s) => s.session?.tenant?.name);
}

/**
 * Returns `true` when the active session is an advisor membership
 * (multi_company_access — the seat-free external accountant). Drives the
 * advisor badge in the CompanySwitcher pill and the persistent AdvisorBanner.
 * An absent `membership_type` (pre-mig-0240 fixtures) reads as `false`.
 */
export function useIsAdvisor(): boolean {
  return useAuthStore(
    (s) => s.session?.user.membership_type === "advisor",
  );
}

/**
 * Returns `true` when the active identity has TOTP two-factor enabled
 * (mfa_two_factor). Identity-level, so it persists across tenant switches.
 * Drives the Settings → Security page's on/off state. An absent
 * `mfa_enabled` (pre-MFA fixtures / pre-tenant) reads as `false`.
 */
export function useMfaEnabled(): boolean {
  return useAuthStore((s) => s.session?.mfa_enabled === true);
}

export { RequirePermission } from "./require-permission";

/**
 * Sign-out per KEN_LAYER_7 §8.8 checklist.
 *
 * The full checklist includes Query cache + IndexedDB + SSE streams +
 * service worker. The pieces this package owns are: the session cookie
 * (invalidated server-side via POST /api/shell/auth/logout) and the
 * Zustand store. The caller wires the rest in the cockpit shell.
 */
export async function signOut(options: {
  /** Endpoint that invalidates the session cookie server-side. */
  logoutUrl?: string;
} = {}): Promise<void> {
  try {
    await fetch(options.logoutUrl ?? "/api/shell/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Network failure — clear local state anyway.
  }
  useAuthStore.getState().clear();
}
